import type {BarElement, BarProps, Chart, ChartType, Plugin} from 'chart.js';
import type {Context as ChartDataLabelsContext} from 'chartjs-plugin-datalabels';

import {createBarChartLabelFormatterContext} from './chartjsBarLabelContext';

/**
 * @see https://chartjs-plugin-datalabels.netlify.app/guide/options.html#formatter
 */
export type BarRightLabelsFormatter = (value: number, context: ChartDataLabelsContext) => string | null;

export type BarRightLabelsOptions = {
    /** Horizontal gap (px) between the bar’s right edge and the label (left edge of text). */
    gap?: number;
    color?: string;
    font?: {
        size?: number;
        weight?: string | number;
        family?: string;
    };
    formatter?: BarRightLabelsFormatter;
};

declare module 'chart.js' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- declaration merge
    interface PluginOptionsByType<TType extends ChartType> {
        barRightLabels?: BarRightLabelsOptions;
    }
}

type ResolvedBarRightLabels = Required<Pick<BarRightLabelsOptions, 'gap' | 'color'>> & {
    font: NonNullable<BarRightLabelsOptions['font']>;
    formatter: NonNullable<BarRightLabelsOptions['formatter']>;
};

const defaultOptions: ResolvedBarRightLabels = {
    gap: 8,
    color: '#4B4B4B',
    font: {
        size: 13,
        weight: 'bold',
        family: 'Moderat',
    },
    formatter: (value: number, _context: ChartDataLabelsContext) => value.toString(),
};

function resolveOptions(options: BarRightLabelsOptions | undefined) {
    return {
        ...defaultOptions,
        ...options,
        font: {...defaultOptions.font, ...options?.font},
        formatter: options?.formatter ?? defaultOptions.formatter,
    };
}

export const barRightLabelsPlugin: Plugin<'bar', BarRightLabelsOptions> = {
    id: 'barRightLabels',

    afterDatasetsDraw(chart: Chart<'bar'>, _args: unknown, pluginOptions: BarRightLabelsOptions | undefined) {
        const options = resolveOptions(pluginOptions);
        const {ctx} = chart;

        ctx.save();

        for (let datasetIndex = 0; datasetIndex < chart.data.datasets.length; datasetIndex++) {
            const dataset = chart.data.datasets[datasetIndex];
            const meta = chart.getDatasetMeta(datasetIndex);
            if (meta.hidden) {
                continue;
            }

            const data = dataset.data as number[];

            for (let dataIndex = 0; dataIndex < data.length; dataIndex++) {
                const raw = data[dataIndex];
                if (raw == null || Number.isNaN(Number(raw))) {
                    continue;
                }

                const element = meta.data[dataIndex] as (BarElement & BarProps) | undefined;
                if (!element) {
                    continue;
                }

                if (!element.horizontal) {
                    continue;
                }

                const value = Number(raw);
                const formatterContext = createBarChartLabelFormatterContext(chart, datasetIndex, dataIndex);
                const formatted = options.formatter(value, formatterContext);
                if (formatted == null) {
                    continue;
                }
                const text = String(formatted);

                const barRight = Math.max(element.x, element.base);
                const centerY = element.y;

                const fontSize = options.font.size ?? 13;
                const fontFamily = options.font.family ?? 'sans-serif';
                const fontWeight = options.font.weight ?? 'bold';
                ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;

                const metrics = ctx.measureText(text);
                const textWidth = metrics.width;

                /** Never move left of this — otherwise the clamp used to pull labels onto long bars */
                const minX = barRight + options.gap;
                let x = minX;
                const canvasRight = chart.width;
                if (x + textWidth > canvasRight - 2) {
                    const fitStart = canvasRight - textWidth - 2;
                    if (fitStart >= minX) {
                        x = fitStart;
                    }
                }

                ctx.fillStyle = options.color;
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';
                ctx.fillText(text, x, centerY);
            }
        }

        ctx.restore();
    },
};
